import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Card, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaDiagnoses, FaHistory, FaClipboardList } from "react-icons/fa";
import moment from "moment";
import { MdAccessTime, MdDateRange } from "react-icons/md";
import Swal from 'sweetalert2';

export default function PetDiagnoseComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petDiagnoses, setPetDiagnoses] = useState([]);
    const [petHistory, setPetHistory] = useState(null);
    const [petPhysicalExam, setPetPhysicalExam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasDiagnoseForAppointment, setHasDiagnoseForAppointment] = useState(false);

    const [openAccordion, setOpenAccordion] = useState(null);
    // Diagnose Modal States
    const [showDiagnoseModal, setShowDiagnoseModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingDiagnose, setEditingDiagnose] = useState(null);
    const [deletingDiagnoseId, setDeletingDiagnoseId] = useState(null);
    const [diagnoseText, setDiagnoseText] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };
    
    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    useEffect(() => {
        fetchAllData();
    }, [appointmentId, petId]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");

            // Fetch diagnoses by pet ID
            const diagnoseResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-diagnose/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            // Fetch history and physical exam from the provided response structure
            // Using the data structure you provided
            const historyResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-history/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            ).catch(() => ({ data: { success: false } }));

            const physicalExamResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-physical-exam/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            ).catch(() => ({ data: { success: false } }));

            let diagnoses = [];
            if (diagnoseResponse.data.success) {
                diagnoses = diagnoseResponse.data.petDiagnose || [];
            } else if (Array.isArray(diagnoseResponse.data)) {
                diagnoses = diagnoseResponse.data || [];
            }
            setPetDiagnoses(diagnoses);
            const hasDiagnose = diagnoses.some(diagnose => String(diagnose?.appointment_id) === String(appointmentId));
            setHasDiagnoseForAppointment(hasDiagnose);

            if (historyResponse.data?.success && historyResponse.data?.petHistory?.length > 0) {
                const historyForAppointment = historyResponse.data.petHistory.find(h => String(h?.appointment_id) === String(appointmentId));
                setPetHistory(historyForAppointment || historyResponse.data.petHistory[0]);
            }

            if (physicalExamResponse.data?.success && physicalExamResponse.data?.petPhysicalExam?.length > 0) {
                const examForAppointment = physicalExamResponse.data.petPhysicalExam.find(e => String(e?.appointment_id) === String(appointmentId));
                setPetPhysicalExam(examForAppointment || physicalExamResponse.data.petPhysicalExam[0]);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            showErrorToast(t('failed_to_load_data'));
            setPetDiagnoses([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString(),
            time: date.toLocaleTimeString(),
            full: date.toLocaleString()
        };
    };

    const formatHistoryText = (history) => {
        if (!history) return 'N/A';
        const lines = history.split('\n');
        return lines.map((line, idx) => {
            if (line.includes(':')) {
                const [label, value] = line.split(':');
                return (
                    <div key={idx} className="mb-2">
                        <strong className="text-muted">{label.trim()}:</strong>{' '}
                        <span className="text-dark">{value?.trim() || 'N/A'}</span>
                    </div>
                );
            }
            return <p key={idx} className="mb-1">{line}</p>;
        });
    };

    const formatPhysicalExamText = (exam) => {
        if (!exam) return 'N/A';
        const lines = exam.split('\n');
        const conditions = [];
        lines.forEach(line => {
            if (line.includes(':')) {
                const [category, ...rest] = line.split(':');
                const value = rest.join(':').trim();
                conditions.push({ category: category.trim(), value });
            } else if (line.trim()) {
                conditions.push({ category: 'General', value: line.trim() });
            }
        });

        if (conditions.length === 0) {
            return <p className="mb-0">{exam}</p>;
        }

        return conditions.map((condition, idx) => (
            <div key={idx} className="mb-2">
                <Badge bg="info" className="me-2 px-2 py-1">{condition.category}</Badge>
                <span className="text-dark">{condition.value}</span>
            </div>
        ));
    };

    // Diagnose Handlers
    const handleAddDiagnose = () => {
        setEditingDiagnose(null);
        setDiagnoseText("");
        setGeneralNote("");
        setExistingNoteId(null);
        setShowDiagnoseModal(true);
    };

    const handleEditDiagnose = (diagnose) => {
        setEditingDiagnose(diagnose);
        setDiagnoseText(diagnose.diagnosis || "");
        if (diagnose.note && Array.isArray(diagnose.note) && diagnose.note.length > 0) {
            setGeneralNote(diagnose.note[0].note || "");
            setExistingNoteId(diagnose.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }
        setShowDiagnoseModal(true);
    };

    const handleDeleteClick = (diagnoseId) => {
        setDeletingDiagnoseId(diagnoseId);
        setShowDeleteModal(true);
    };

    const handleDeleteDiagnose = async () => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-diagnose-delete/${deletingDiagnoseId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setShowDeleteModal(false);
                fetchAllData();
                showSuccessToast(t('Diagnose deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting diagnose:", error);
            showErrorToast(t('failed_to_delete_diagnose'));
        } finally {
            setSubmitting(false);
            setDeletingDiagnoseId(null);
        }
    };

    const handleDiagnoseSubmit = async (e) => {
        e.preventDefault();
        if (!diagnoseText.trim()) {
            showWarningToast(t('please enter diagnose'));
            return;
        }
        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingDiagnose
                ? `${process.env.REACT_APP_API_URL}/pet-diagnose-update/${editingDiagnose.id}`
                : `${process.env.REACT_APP_API_URL}/pet-diagnose`;

            const payload = {
                appointment_id: appointmentId,
                diagnosis: diagnoseText.trim()
            };

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.success || response.data.id) {
                setShowDiagnoseModal(false);
                fetchAllData();
                showSuccessToast(editingDiagnose ? t('Diagnose updated successfully!') : t('Diagnose added successfully!'));
            }
        } catch (error) {
            console.error("Error saving diagnose:", error);
            showErrorToast(error.response?.data?.message || t('failed to save diagnose'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading data')}</p>
            </div>
        );
    }

    return (
        <div>
            {/* Cards Section for Pet History and Physical Exam - Only Display */}


            {/* Diagnoses Section */}
            <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <h6 className="mb-0 text-muted d-flex align-items-center gap-2">
                        <FaDiagnoses className="text-primary" />
                        {t('Diagnoses List')}
                    </h6>
                    {isAppointmentOpen && !hasDiagnoseForAppointment &&
                        hasPermission("Appointment Diagnose Create") && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleAddDiagnose}
                                className="d-flex align-items-center gap-2 rounded-pill px-3"
                            >
                                <FaPlus size={12} /> {t('add diagnose')}
                            </Button>
                        )}
                </div>

                {!petDiagnoses || petDiagnoses.length === 0 ? (
                    <Alert variant="info" className="text-center py-4">
                        <FaDiagnoses size={30} className="mb-2 text-muted" />
                        <p className="mb-0">{t('no diagnoses found')}</p>
                    </Alert>
                ) : (
                    <Accordion
                        activeKey={openAccordion}
                        onSelect={(key) => setOpenAccordion(key === openAccordion ? null : key)}
                        className="pet-diagnose-accordion"
                    >
                        {petDiagnoses.map((diagnose) => {
                            if (!diagnose) return null;
                            const dateTime = formatDateTime(diagnose.created_at);
                            const noteText = diagnose.note && Array.isArray(diagnose.note) && diagnose.note.length > 0
                                ? diagnose.note[0].note
                                : null;

                            return (
                                <Accordion.Item eventKey={String(diagnose.id)} key={diagnose.id} className="mb-3 border rounded">
                                    <Accordion.Header className="bg-light">
                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                <div className="text-muted small">
                                                    <strong>{diagnose.appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                                </div>
                                                <div className="text-muted small">
                                                    👨‍⚕️<strong>Doctor</strong>: {diagnose.doctor?.name || 'Unknown Doctor'}
                                                </div>
                                                <div className="d-flex align-items-center gap-3 text-muted small">
                                                    <span className="d-flex align-items-center gap-1">
                                                        <MdDateRange size={14} />
                                                        {moment(dateTime.date).format("DD MMM YYYY")}
                                                    </span>

                                                    <span className="d-flex align-items-center gap-1">
                                                        <MdAccessTime size={14} />
                                                        {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                    </span>
                                                </div>

                                                <Badge bg="info" className="px-3 py-2">
                                                    {diagnose.diagnosis?.substring(0, 50)}...
                                                </Badge>
                                            </div>
                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                {hasPermission("Appointment Diagnose edit") && (
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => handleEditDiagnose(diagnose)}
                                                        className="d-flex align-items-center gap-1"
                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                    >
                                                        <FaEdit size={12} />
                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                    </Button>
                                                )}
                                                {/* <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteClick(diagnose.id)}
                                                    className="d-flex align-items-center gap-1 bg-danger"
                                                >
                                                    <FaTrash size={12} />
                                                    <span className="d-none d-sm-inline">{t('delete')}</span>
                                                </Button> */}
                                            </div>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body className="bg-white">
                                        <Row className="g-4">
                                            <Col md={12}>
                                                <div className="border-bottom pb-2 mb-3">
                                                    <h6 className="mb-0 d-flex align-items-center gap-2">
                                                        <FaStethoscope className="text-primary" />
                                                        {t('diagnose details')}
                                                    </h6>
                                                </div>
                                                <div className="p-3 bg-light rounded">
                                                    <div className="mb-2">
                                                        <strong className="text-muted">{t('diagnose')}:</strong>
                                                        <p className="mb-0 mt-1 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                                                            {diagnose.diagnosis}
                                                        </p>
                                                    </div>
                                                    {noteText && (
                                                        <div className="mt-3 pt-2 border-top">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <FaFileAlt className="text-success" />
                                                                <strong className="text-muted">{t('note')}:</strong>
                                                            </div>
                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                )}
            </div>

            {/* Diagnose Add/Edit Modal */}
            <Modal show={showDiagnoseModal} onHide={() => setShowDiagnoseModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-3 border-0">
                    <Modal.Title className="fw-bold">
                        {editingDiagnose ? t('Edit Diagnose') : t('Add Diagnose')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleDiagnoseSubmit}>
                    <Modal.Body className="pt-0">
                        <Card className="border-0 shadow-sm bg-light">
                            <Card.Body>
                                <Row className="mb-4 g-3">
                                    {/* Pet History Card - Display Only */}
                                    <Col md={6}>
                                        <Card className="h-100 border shadow-sm" style={{ borderRadius: '12px' }}>
                                            <Card.Body className="p-4">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div>
                                                        <h5 className="mb-0 fw-bold">{t('Pet History')}</h5>
                                                        <small className="text-muted">{t('Medical History Record')}</small>
                                                    </div>
                                                </div>
                                                {petHistory && petHistory.history ? (

                                                    <div className="text-dark">
                                                        {formatHistoryText(petHistory.history)}

                                                    </div>
                                                ) : (
                                                    <div className="bg-light rounded-3 p-4 text-center">
                                                        <FaHistory size={40} className="text-muted mb-2 opacity-50" />
                                                        <p className="text-muted mb-0">{t('No history record found')}</p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* Pet Physical Exam Card - Display Only */}
                                    <Col md={6}>
                                        <Card className="h-100 border shadow-sm" style={{ borderRadius: '12px' }}>
                                            <Card.Body className="p-4">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div>
                                                        <h5 className="mb-0 fw-bold">{t('Physical Examination')}</h5>
                                                        <small className="text-muted">{t('Clinical Findings')}</small>
                                                    </div>
                                                </div>
                                                {petPhysicalExam && petPhysicalExam.physical_exam ? (

                                                    <div className="text-dark">
                                                        {formatPhysicalExamText(petPhysicalExam.physical_exam)}

                                                    </div>
                                                ) : (
                                                    <div className="bg-light rounded-3 p-4 text-center">
                                                        <FaStethoscope size={40} className="text-muted mb-2 opacity-50" />
                                                        <p className="text-muted mb-0">{t('No physical exam record found')}</p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        <FaStethoscope className="me-2 text-primary" />
                                        {t('Clinical Assessment')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={diagnoseText}
                                        onChange={(e) => setDiagnoseText(e.target.value)}
                                        placeholder={t('Enter detailed clinical assessment and diagnosis...')}
                                        required
                                        className="rounded-3 border-0"
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Modal.Body>
                    <Modal.Footer className="border-0 p-2">
                        <Button
                            variant="secondary" onClick={() => setShowDiagnoseModal(false)} className="rounded-pill px-4">
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting}
                            className="rounded-pill px-4"
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingDiagnose ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="p-3 border-0">
                    <Modal.Title className="fw-bold">{t('confirm_delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="rounded-circle bg-danger bg-opacity-10 p-3 d-inline-flex mb-3">
                        <FaTrash size={40} className="text-danger" />
                    </div>
                    <h5 className="mb-2">{t('delete_diagnose_confirmation')}</h5>
                    <p className="text-muted mb-0">{t('this_action_cannot_be_undone')}</p>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteDiagnose}
                        disabled={submitting}
                        className="rounded-pill px-4"
                    >
                        {submitting ? t('deleting') : t('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}